import { useMutation } from '@apollo/client';
import { useSetRecoilState } from 'recoil';

import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import UpdateContractLinesByIntervalAndDateMutation from
  '@/graphql/contracts/updateContractLinesByIntervalAndDate.mutation.gql';
import { UpdateContractLinesByIntervalAndDate, UpdateContractLinesByIntervalAndDateVariables }
  from '@/graphql/contracts/_generated_/updateContractLinesByIntervalAndDate.mutation';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { ALL_ITEMS } from '@/config/common.config';
import { notificationService } from '@/services/notification/notification.client';
import { rightsidePanel_isOpen } from '../../../RightPanel.state';
import {
  clearLinesSelection,
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { clearVariableLinesSelection } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';

type BillingIntervalInput = {
  contractId: UUID,
  contractLines: { contractLineID: UUID }[]
  billingInterval: string,
};

export default function useUpdateBillingInterval(contractId: UUID, variableSelected: boolean) {
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);

  const handleSuccess = () => {
    clearLinesSelection();
    clearVariableLinesSelection();
    setIsOpenRightPanel(false);
  };

  const [_updateBillingInterval, { loading }] = useMutation<UpdateContractLinesByIntervalAndDate,
  UpdateContractLinesByIntervalAndDateVariables>(
    UpdateContractLinesByIntervalAndDateMutation,
    {
      client: apolloClientContracts,
      awaitRefetchQueries: true,
      refetchQueries: [
        variableSelected ?
          {
            query: GetPaginatedFilteredVariableContractLinesQuery,
            variables: {
              filter: { headerPrimaryKey: { eq: contractId } },
              first: ALL_ITEMS,
            }
          } :
          {
            query: GetBCContractLinesQuery,
            variables: {
              filter: {
                contractId: { eq: contractId }
              },
              first: ALL_ITEMS
            }
          },
      ],
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      onCompleted: () => {
        handleSuccess();
        notificationService.open({
          title: 'Billing interval successfully updated',
          text: 'Billing Interval',
          type: 'success'
        });
      }
    }
  );

  const updateBillingInterval = (input: BillingIntervalInput) => {
    _updateBillingInterval({
      variables: { input }
    });
  };

  return {
    updateBillingInterval,
    isLoadingUpdateBillingInterval: loading,
  } as const;
}
